export type PartnerTaxProfile = {
  w2Self: number;
  w2Spouse: number;
  dependents: number;
  extraAnnualCredits: number;
};

export type AdvancedTaxEstimate = {
  selfEmploymentTax: number;
  incomeTaxAfterCredits: number;
  totalEstimatedFederalTax: number;
  effectiveRateOnLlc: number;
  qbiDeduction: number;
};

/** MFJ-oriented planning brackets (simplified; not a filing engine). */
const MFJ_BRACKETS = [
  { upto: 23850, rate: 0.1 },
  { upto: 96950, rate: 0.12 },
  { upto: 206700, rate: 0.22 },
  { upto: 394600, rate: 0.24 },
  { upto: Number.POSITIVE_INFINITY, rate: 0.32 },
] as const;

const STANDARD_DEDUCTION_MFJ = 32200;
const CHILD_CREDIT_PER_DEPENDENT = 2200;

function roundMoney(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

export const DEFAULT_TAX_PROFILE: PartnerTaxProfile = {
  w2Self: 40000,
  w2Spouse: 35000,
  dependents: 2,
  extraAnnualCredits: 0,
};

export function clampNonNegative(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, value);
}

export function estimateFederalIncomeTax(taxableIncome: number): number {
  let remaining = clampNonNegative(taxableIncome);
  let last = 0;
  let tax = 0;
  for (const bracket of MFJ_BRACKETS) {
    const slice = Math.max(0, Math.min(remaining, bracket.upto - last));
    tax += slice * bracket.rate;
    remaining -= slice;
    last = bracket.upto;
    if (remaining <= 0) break;
  }
  return tax;
}

/**
 * Rough federal planning estimate for one partner household (MFJ assumptions).
 * `llcShareNet` is the partner's LLC profit share for the modeled period/year slice.
 */
export function estimateAdvancedTax(
  profile: PartnerTaxProfile,
  llcShareNet: number
): AdvancedTaxEstimate {
  const w2Total = clampNonNegative(profile.w2Self) + clampNonNegative(profile.w2Spouse);
  const llc = clampNonNegative(llcShareNet);
  const seBase = llc * 0.9235;
  const selfEmploymentTax = seBase * 0.153;
  const halfSeDeduction = selfEmploymentTax / 2;
  const qbiDeduction = Math.max(0, llc * 0.2);
  const agi = w2Total + llc - halfSeDeduction;
  const taxableIncome = Math.max(0, agi - STANDARD_DEDUCTION_MFJ - qbiDeduction);
  const incomeTaxBeforeCredits = estimateFederalIncomeTax(taxableIncome);
  const credits =
    clampNonNegative(profile.dependents) * CHILD_CREDIT_PER_DEPENDENT +
    clampNonNegative(profile.extraAnnualCredits);
  const incomeTaxAfterCredits = Math.max(0, incomeTaxBeforeCredits - credits);
  const totalEstimatedFederalTax = incomeTaxAfterCredits + selfEmploymentTax;
  const effectiveRateOnLlc = llc > 0 ? totalEstimatedFederalTax / llc : 0;

  return {
    selfEmploymentTax: roundMoney(selfEmploymentTax),
    incomeTaxAfterCredits: roundMoney(incomeTaxAfterCredits),
    totalEstimatedFederalTax: roundMoney(totalEstimatedFederalTax),
    effectiveRateOnLlc,
    qbiDeduction: roundMoney(qbiDeduction),
  };
}

export type PartnerTaxReserve = {
  name: string;
  sharePct: number;
  weight: number;
  preTaxShare: number;
  taxReserve: number;
  net: number;
  effectiveRateOnLlc: number;
};

export function allocateAdvancedTaxReserves(args: {
  profitBeforeTax: number;
  partners: Array<{ name: string; sharePct: number; weight: number }>;
  profiles: PartnerTaxProfile[];
  /** When monthly, annualize LLC share for brackets then scale reserve back. */
  period: 'project' | 'monthly';
}): {
  taxReserve: number;
  effectiveRate: number;
  partnerReserves: PartnerTaxReserve[];
} {
  const profit = Math.max(0, args.profitBeforeTax);
  const partnerReserves = args.partners.map((partner, index) => {
    const profile = args.profiles[index] ?? DEFAULT_TAX_PROFILE;
    const preTaxShare = profit * partner.weight;
    const annualShare = args.period === 'monthly' ? preTaxShare * 12 : preTaxShare;
    const estimate = estimateAdvancedTax(profile, annualShare);
    const taxReserve =
      args.period === 'monthly'
        ? estimate.totalEstimatedFederalTax / 12
        : estimate.totalEstimatedFederalTax;
    return {
      name: partner.name,
      sharePct: partner.sharePct,
      weight: partner.weight,
      preTaxShare: roundMoney(preTaxShare),
      taxReserve: roundMoney(Math.max(0, taxReserve)),
      net: roundMoney(Math.max(0, preTaxShare - taxReserve)),
      effectiveRateOnLlc: estimate.effectiveRateOnLlc,
    };
  });

  const taxReserve = roundMoney(partnerReserves.reduce((sum, row) => sum + row.taxReserve, 0));
  const effectiveRate = profit > 0 ? taxReserve / profit : 0;

  return { taxReserve, effectiveRate, partnerReserves };
}
